import sanitizeHtml from 'sanitize-html';
import { ensureUrlHasScheme } from './ensure-url-has-scheme';

const FURTHER_BLANK_ATTR = 'data-further-blank';

// Matches a single paragraph and captures:
// - attrs: any attributes on the <p> tag
// - body: the inner HTML of the paragraph
const PARAGRAPH_CAPTURE_REGEX = /<p([^>]*)>([\s\S]*?)<\/p>/gi;

// Matches any "blank" paragraph content we want to treat as whitespace, including NBSP variants.
const WHITESPACE_OR_NBSP_REGEX = /(\s|&nbsp;|&#160;|&#xA0;|&#x00A0;|\u00A0)+/gi;

type Options = {
  openExternalLinksInNewTab?: boolean;
  allowImages?: boolean;
};

const defaultOptions: Options = {
  openExternalLinksInNewTab: false,
  allowImages: true,
};

const sanitiseHtml = (
  dirtyHtml?: string | null,
  options: Options = defaultOptions,
) => {
  if (!dirtyHtml) {
    return undefined;
  }

  // Mark NBSP-only paragraphs before normalising NBSPs to regular spaces.
  // This lets downstream PDF rendering treat these as intentional blank lines.
  let cleanedHtml = dirtyHtml.replace(
    PARAGRAPH_CAPTURE_REGEX,
    (match, attrs: string, body: string) => {
      const hasNbsp =
        body.includes('\u00A0') ||
        body.includes('&nbsp;') ||
        body.includes('&#160;') ||
        body.includes('&#xA0;') ||
        body.includes('&#x00A0;');
      const isOnlyWhitespaceOrNbsp = !body.replace(WHITESPACE_OR_NBSP_REGEX, '')
        .length;

      if (
        hasNbsp &&
        isOnlyWhitespaceOrNbsp &&
        !attrs.includes(FURTHER_BLANK_ATTR)
      ) {
        return `<p${attrs} ${FURTHER_BLANK_ATTR}="1"></p>`;
      }
      return match;
    },
  );

  cleanedHtml = cleanedHtml
    .replace(/\u00A0/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/&#xA0;/gi, ' ')
    .replace(/&#x00A0;/gi, ' ');

  return sanitizeHtml(cleanedHtml, {
    allowedTags: [
      'article',
      'aside',
      'footer',
      'header',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'main',
      'nav',
      'section',
      'blockquote',
      'div',
      'hr',
      'li',
      'main',
      'ol',
      'p',
      'pre',
      'ul',
      'a',
      'b',
      'br',
      'code',
      'em',
      'i',
      'u',
      'small',
      'span',
      'strong',
      'caption',
      'col',
      'colgroup',
      'table',
      'tbody',
      'td',
      'tfoot',
      'th',
      'thead',
      'tr',
      ...(options.allowImages ? ['img'] : []),
    ],
    allowedAttributes: {
      '*': ['style'],
      img: ['src', 'alt', 'width', 'height'],
      a: ['href', 'name', 'target', 'rel'],
      p: [FURTHER_BLANK_ATTR],
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel', 'data'],
    allowedSchemesAppliedToAttributes: ['href', 'src'],
    disallowedTagsMode: 'discard',
    selfClosing: ['img', 'br', 'hr'],
    transformTags: {
      a: (_, attribs) => {
        return {
          tagName: 'a',
          attribs: {
            ...attribs,
            ...(options.openExternalLinksInNewTab && { target: '_blank' }),
            href: ensureUrlHasScheme(attribs['href']),
          },
        };
      },
    },
  });
};

export default sanitiseHtml;
